import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {MAT_MOMENT_DATE_FORMATS, MomentDateAdapter} from '@angular/material-moment-adapter';
import { EUCountryCode, SapAddress } from 'libs/sap/src/lib/models/sap.model';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment/moment';
import { ACTION } from 'libs/tm-common/src/lib/constants/subcomponent.actions';
import { TreatmentsService } from 'libs/treatments/src/lib/services';

const MAX_LENGTH_CONTACT_NAME = 22;
const MAX_LENGTH_SPECIAL_INSTRUCTIONS = 57;

@Component({
	selector: 'ux-ups-pickup',
	templateUrl: './ups-pickup.component.html',
	styleUrls: ['./ups-pickup.component.scss'],
	providers: [
		{provide: MAT_DATE_LOCALE, useValue: ''},
		{provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
		{provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS}
	]
})
export class UpsPickupComponent implements OnInit, OnChanges, AfterViewInit {

	@Input({required: true}) shippingAddresses: SapAddress[];
	@Input() user;
	@Input() treatment;
	@Input() loading = false;
	@Input() prn = null;
	@Output() action = new EventEmitter();

	upsForm: FormGroup = this.formBuilder.group( {
		address: this.formBuilder.group({
			CompanyName: ['', Validators.required], //set by mat-select
			AddressLine: ['', Validators.required], //set by mat-select
			PostalCode: ['', Validators.required],  //set by mat-select
			City: ['', Validators.required],  		//set by mat-select
			CountryCode: ['', Validators.required], //set by mat-select
			ContactName: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_CONTACT_NAME)]],
			Phone: this.formBuilder.group({
			  Number: ['', [Validators.required, this.validatePhoneNumber()]]
			})
		  }),
		  dateTime: this.formBuilder.group({
			PickupDate: ['', Validators.required],
			ReadyTime: ['1000', [Validators.required, this.validateTimeRange()]],
			CloseTime: ['1700', [Validators.required, this.validateTimeRange()]]
		  }),
		  specialInstruction: ['', Validators.maxLength(MAX_LENGTH_SPECIAL_INSTRUCTIONS)]
	});


	selectedAddressName: string;

	translateSubscription$: Subscription;

	hasPendingUpsPickupRequest: boolean = false;
	maxLengthContactName = MAX_LENGTH_CONTACT_NAME;
	maxLengthSpecialInstructions = MAX_LENGTH_SPECIAL_INSTRUCTIONS;

	constructor(@Inject('CONFIG') public config,
				private formBuilder: FormBuilder,
				private dateAdapter: DateAdapter<any>,
				private translate: TranslateService,
				private treatmentsService: TreatmentsService,
				private cdr: ChangeDetectorRef,
				public dialog: MatDialog) {
	}

	ngOnInit() {
		this.pickupDate.patchValue(moment(new Date()).add(1, 'days').format('YYYYMMDD'));
		// set default locale...
		this.dateAdapter.setLocale(this.config.locale);
		// ... and subscribe language change
		this.translateSubscription$ = this.translate.onLangChange.subscribe(e => {
			this.dateAdapter.setLocale(e.lang);
		});

		this.hasPendingUpsPickupRequest = this.treatmentsService.hasPendingUpsPickupRequest(this.treatment);
	}

	ngAfterViewInit() {
		this.contactName.markAsTouched();
		this.phoneNumber.markAsTouched();
		this.cdr.detectChanges();
	}
	ngOnChanges() {
		this.selectedAddressName = this.shippingAddresses.at(0).AddressName;
		this.buildAddress();
	}
	onSubmit() {
		//parse date to required string format by ups api
		//only applies if the date will be changed in the form, since it
		//is initialized in the correct format (changing creates a 'moment' object)
		if(this.pickupDate.value && moment.isMoment(this.pickupDate.value)){
			this.pickupDate.patchValue(this.pickupDate.value.format('YYYYMMDD'));
		}
		this.action.emit({ type: ACTION.SUBMIT, payload: this.upsForm.value});
	}

	onAddressSelection() {
		this.buildAddress();
	}

	buildAddress() {
		const addr: SapAddress = this.shippingAddresses.find(addr => addr.AddressName === this.selectedAddressName);

		this.companyName.patchValue(addr.AddressName2 + (addr.AddressName3 ? `, ${addr.AddressName3}` : ""));
		this.addressLine.patchValue(addr.Street);
		this.postalCode.patchValue(addr.ZipCode);
		this.city.patchValue(addr.City);
		this.countryCode.patchValue(this.getTextForCountryCode(addr.Country));
	}

	protected getTextForCountryCode(Country: string) {
		return Country === EUCountryCode.Greece_EL ? EUCountryCode.Greece : Country;
	}

	protected getTomorrowDate(): Date {
		const tomorrowDate = new Date();
		tomorrowDate.setDate(tomorrowDate.getDate() + 1)
		return tomorrowDate;
	}

	 /**
	 * Initializes options that can be selected for the time range
	 * e.g. start: 900 end: 1000 would result in '[900,915,930,945,1000]'
	 * @param start - start of time range
	 * @param end - end of time range
	 * @returns {string[]} - array with time options
	 */
	protected getTimeRange(start: number, end: number): string[]{
		const options = [];

		for (let i = start; i <= end; i += 100) {
			for (let n = 0; n < 60; n += 15) {
				const hourStr = i < 1000 ? '0' + i / 100 : i / 100;
				const minuteStr = n ? n + '' : '0' + n;
				const option = hourStr + minuteStr;
				options.push(option);
				if (i === end) {
					break;
				}
			}
		}
		return options;
	}

	private validatePhoneNumber() : ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			const phonePattern = /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,5})|(\(?\d{2,6}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;
			return phonePattern.test(value) ? null : { phoneNumber: { value: control.value } };
		};
	}

	/**
	 * Validates if the time range control field values have at least a gap of 2 hours
	 */
	private validateTimeRange(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const parent = control.parent;
			if (!parent){
				return null;
			}

			const readyTime = this.readyTime?.value;
			const closeTime = this.closeTime?.value;

			if (!readyTime || !closeTime) {
				return null; // If either time is not provided, don't validate
			}

			const readyTimeHours = parseInt(readyTime.substring(0, 2), 10);
			const readyTimeMinutes = parseInt(readyTime.substring(2, 4), 10);

			const closeTimeHours = parseInt(closeTime.substring(0, 2), 10);
			const closeTimeMinutes = parseInt(closeTime.substring(2, 4), 10);

			// Convert times to minutes for easy comparison
			const readyTimeInMinutes = readyTimeHours * 60 + readyTimeMinutes;
			const closeTimeInMinutes = closeTimeHours * 60 + closeTimeMinutes;

			// Check if CloseTime is at least 120 minutes (2 hours) after ReadyTime
			const differenceInMinutes = closeTimeInMinutes - readyTimeInMinutes;

			if (differenceInMinutes >= 120) {
				return null;
			} else {
				return { timeRangeInvalid: true };
			}
		};
	}

	get companyName() {
		return this.upsForm.get('address.CompanyName');
	}

	get addressLine() {
		return this.upsForm.get('address.AddressLine');
	}

	get postalCode() {
		return this.upsForm.get('address.PostalCode');
	}

	get city() {
		return this.upsForm.get('address.City');
	}

	get countryCode() {
		return this.upsForm.get('address.CountryCode');
	}

	get contactName() {
		return this.upsForm.get('address.ContactName');
	}

	get phoneNumber() {
		return this.upsForm.get('address.Phone.Number');
	}

	get pickupDate() {
		return this.upsForm.get('dateTime.PickupDate');
	}

	get readyTime() {
		return this.upsForm.get('dateTime.ReadyTime');
	}

	get closeTime() {
		return this.upsForm.get('dateTime.CloseTime');
	}
}
